// One email check for every Escalation Management email field (merchant CC list, weekly summary
// recipients, Create Ticket). proship_mongo's EscalationEmails applies the same rules on save.

const LOCAL = /^[A-Za-z0-9_%+-]+(\.[A-Za-z0-9_%+-]+)*$/;
const LABEL = /^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?$/;
const TLD = /^[A-Za-z]{2,24}$/;

// Domains people mistype, and the spellings of each that are real.
const KNOWN = {
    prozo: ["prozo.com", "prozo.in"],
    gmail: ["gmail.com"],
    yahoo: ["yahoo.com", "yahoo.co.in", "yahoo.in"],
    outlook: ["outlook.com", "outlook.in"],
    hotmail: ["hotmail.com", "hotmail.co.uk"],
    rediffmail: ["rediffmail.com"],
    icloud: ["icloud.com"],
};
// Real providers that sit one letter away from a name above (ymail ~ gmail, mail ~ gmail).
const REAL_LOOKALIKES = ["ymail", "mail", "email", "gmx", "zoho", "aol", "proton", "protonmail"];
// ".con", ".cmo" and friends — never real endings.
const TLD_TYPOS = ["con", "cmo", "comm", "coom", "cpm", "vom", "xom", "comom", "cim", "cok", "comn", "ocm"];

const distance = (a, b) => {
    const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
    for (let j = 1; j <= b.length; j++) dp[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) dp[i][j] = Math.min(dp[i][j], dp[i - 2][j - 2] + 1);
        }
    }
    return dp[a.length][b.length];
};

/** The domain the person most likely meant, or null when the domain looks deliberate. */
const likelyTypo = (domain) => {
    const labels = domain.split(".");
    const name = labels[0];
    const tld = labels[labels.length - 1];
    if (KNOWN[name]) return KNOWN[name].includes(domain) ? null : KNOWN[name][0];
    if (REAL_LOOKALIKES.includes(name)) return TLD_TYPOS.includes(tld) ? `${name}.com` : null;
    const near = Object.keys(KNOWN).find((k) => distance(name, k) <= (k.length >= 6 ? 2 : 1));
    if (near) return KNOWN[near][0];
    return TLD_TYPOS.includes(tld) ? [...labels.slice(0, -1), "com"].join(".") : null;
};

/** null when the address is fine, otherwise what's wrong with it. */
export const emailProblem = (raw) => {
    const email = (raw || "").trim();
    const at = email.lastIndexOf("@");
    if (at < 1 || email.indexOf("@") !== at) return `"${email}" isn't a valid email`;
    const local = email.slice(0, at);
    const domain = email.slice(at + 1).toLowerCase();
    const labels = domain.split(".");
    if (email.length > 254 || local.length > 64 || !LOCAL.test(local) || labels.length < 2
        || !labels.slice(0, -1).every((l) => LABEL.test(l)) || !TLD.test(labels[labels.length - 1])) {
        return `"${email}" isn't a valid email`;
    }
    const meant = likelyTypo(domain);
    return meant ? `Check "${email}" — did you mean ${local}@${meant}?` : null;
};

export const splitEmails = (text) => String(text || "").split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);

/** First problem in a comma/space separated list, or null. */
export const emailListProblem = (text) => {
    for (const e of splitEmails(text)) {
        const p = emailProblem(e);
        if (p) return p;
    }
    return null;
};
